import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Server,
  Clock,
  HardDrive,
  Copy,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  HelpCircle,
  Key,
  Settings,
  Download,
  FileSpreadsheet,
  FileText
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  success: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-500/20", label: "Succes" },
  warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-500/20", label: "Attention" },
  error: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/20", label: "Erreur" },
  running: { icon: Loader2, color: "text-blue-500 animate-spin", bg: "bg-blue-500/20", label: "En cours" },
  unknown: { icon: HelpCircle, color: "text-slate-500", bg: "bg-slate-500/20", label: "Inconnu" }
};

const formatBytes = (bytes) => {
  if (!bytes) return "-";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDuration = (seconds) => {
  if (!seconds) return "-";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const ServerDetail = () => {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [server, setServer] = useState(null);
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState([]);
  const [agentConfig, setAgentConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [newFrequency, setNewFrequency] = useState(15);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [serverRes, historyRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/servers/${serverId}`, { headers }),
        axios.get(`${API_URL}/api/servers/${serverId}/history`, { headers }),
        axios.get(`${API_URL}/api/servers/${serverId}/agent-config`, { headers })
      ]);

      setServer(serverRes.data);
      setHistory(historyRes.data);
      setAgentConfig(configRes.data);
      setNewFrequency(configRes.data.frequency_minutes);

      // Fetch client info
      const clientRes = await axios.get(`${API_URL}/api/clients/${serverRes.data.client_id}`, { headers });
      setClient(clientRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [serverId]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copie dans le presse-papier");
  };

  const regenerateApiKey = async () => {
    try {
      const headers = getAuthHeader();
      const res = await axios.post(`${API_URL}/api/servers/${serverId}/regenerate-api-key`, {}, { headers });
      setAgentConfig({ ...agentConfig, api_key: res.data.api_key });
      toast.success("Nouvelle cle API generee");
    } catch (error) {
      toast.error("Erreur lors de la regeneration");
    }
  };

  const updateFrequency = async () => {
    try {
      const headers = getAuthHeader();
      await axios.put(`${API_URL}/api/servers/${serverId}/agent-config?frequency_minutes=${newFrequency}`, {}, { headers });
      setAgentConfig({ ...agentConfig, frequency_minutes: newFrequency });
      setConfigDialogOpen(false);
      toast.success("Frequence mise a jour");
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  if (!server) return null;

  const status = statusConfig[server.last_backup_status] || statusConfig.unknown;
  const StatusIcon = status.icon;

  // Prepare chart data
  const chartData = history
    .slice(0, 20)
    .reverse()
    .map((h, idx) => ({
      index: idx,
      size: h.backup_size ? h.backup_size / (1024 * 1024 * 1024) : 0,
      duration: h.duration ? h.duration / 60 : 0,
      status: h.status
    }));

  return (
    <div className="space-y-6" data-testid="server-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-white font-mono">{server.hostname}</h1>
          <p className="text-slate-400">
            {client?.name} {server.ip_address && `• ${server.ip_address}`}
          </p>
        </div>
      </div>

      {/* Status card */}
      <div className="glass-card rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-xl ${status.bg}`}>
              <StatusIcon className={status.color} size={32} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Statut actuel</p>
              <p className={`text-2xl font-bold ${status.color}`}>{status.label}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-slate-400 flex items-center gap-1">
                <Clock size={14} /> Dernier backup
              </p>
              <p className="font-mono text-white">
                {server.last_backup_time
                  ? formatDistanceToNow(new Date(server.last_backup_time), { addSuffix: true, locale: fr })
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 flex items-center gap-1">
                <HardDrive size={14} /> Taille
              </p>
              <p className="font-mono text-white">{formatBytes(server.last_backup_size)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Duree</p>
              <p className="font-mono text-white">{formatDuration(server.last_backup_duration)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Job</p>
              <p className="font-mono text-white truncate">{server.last_backup_job_name || "-"}</p>
            </div>
          </div>
        </div>

        {server.last_backup_message && (
          <div className="mt-4 p-3 rounded bg-slate-900/50">
            <p className="text-sm text-slate-300">{server.last_backup_message}</p>
          </div>
        )}
      </div>

      {/* Agent config */}
      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
            <Key size={20} className="text-indigo-400" />
            Configuration de l&apos;agent
          </h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfigDialogOpen(true)}
            className="bg-slate-800 hover:bg-slate-700"
            data-testid="config-btn"
          >
            <Settings size={16} className="mr-2" />
            Configurer
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-400">Server ID</Label>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={server.id}
                className="bg-slate-950 border-slate-700 text-white font-mono text-sm"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => copyToClipboard(server.id)}
                className="text-slate-400 hover:text-white"
                data-testid="copy-server-id-btn"
              >
                <Copy size={16} />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-400">API Key</Label>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={agentConfig?.api_key || ""}
                type="password"
                className="bg-slate-950 border-slate-700 text-white font-mono text-sm"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => copyToClipboard(agentConfig?.api_key)}
                className="text-slate-400 hover:text-white"
                data-testid="copy-api-key-btn"
              >
                <Copy size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={regenerateApiKey}
                className="text-slate-400 hover:text-white"
                data-testid="regenerate-key-btn"
              >
                <RefreshCw size={16} />
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 rounded bg-indigo-500/10 border border-indigo-500/20">
          <p className="text-sm text-indigo-300">
            Frequence de rapport: <span className="font-mono font-bold">{agentConfig?.frequency_minutes} minutes</span>
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Agent vu pour la derniere fois:{" "}
            {server.agent_last_seen
              ? formatDistanceToNow(new Date(server.agent_last_seen), { addSuffix: true, locale: fr })
              : "Jamais"}
          </p>
        </div>
      </div>

      {/* Config dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Configuration de l&apos;agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Frequence de rapport (minutes)</Label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={newFrequency}
                onChange={(e) => setNewFrequency(parseInt(e.target.value) || 15)}
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="frequency-input"
              />
            </div>
            <Button
              onClick={updateFrequency}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-frequency-btn"
            >
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass-card rounded-lg p-6">
            <h3 className="font-heading font-bold text-white mb-4">Taille des sauvegardes (GB)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <XAxis dataKey="index" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1E293B", border: "1px solid #334155" }}
                  labelStyle={{ color: "#F8FAFC" }}
                />
                <Line type="monotone" dataKey="size" stroke="#6366F1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="glass-card rounded-lg p-6">
            <h3 className="font-heading font-bold text-white mb-4">Duree des sauvegardes (min)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <XAxis dataKey="index" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1E293B", border: "1px solid #334155" }}
                  labelStyle={{ color: "#F8FAFC" }}
                />
                <Line type="monotone" dataKey="duration" stroke="#10B981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* History table */}
      <div className="glass-card rounded-lg overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="font-heading font-bold text-white">Historique des sauvegardes</h3>
          <div className="flex items-center gap-2">
            <a
              href={`${API_URL}/api/export/servers/${serverId}/csv?days=30`}
              onClick={(e) => {
                e.preventDefault();
                const headers = getAuthHeader();
                fetch(`${API_URL}/api/export/servers/${serverId}/csv?days=30`, { headers })
                  .then(r => r.blob())
                  .then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `export_${server?.hostname || "server"}.csv`; a.click(); });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white text-xs transition-colors cursor-pointer"
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet size={14} /> CSV
            </a>
            <a
              href={`${API_URL}/api/export/servers/${serverId}/pdf?days=30`}
              onClick={(e) => {
                e.preventDefault();
                const headers = getAuthHeader();
                fetch(`${API_URL}/api/export/servers/${serverId}/pdf?days=30`, { headers })
                  .then(r => r.blob())
                  .then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `rapport_${server?.hostname || "server"}.pdf`; a.click(); });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 hover:text-white text-xs transition-colors cursor-pointer"
              data-testid="export-pdf-btn"
            >
              <FileText size={14} /> PDF
            </a>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Aucun historique disponible
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Job</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">VM / Cible</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Taille</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Duree</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Message</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => {
                  const itemStatus = statusConfig[item.status] || statusConfig.unknown;
                  const ItemIcon = itemStatus.icon;
                  return (
                    <tr key={item.id} className="border-t border-slate-800">
                      <td className="p-4 font-mono text-sm text-slate-300">
                        {new Date(item.backup_time).toLocaleString("fr-FR")}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${itemStatus.bg} ${itemStatus.color}`}>
                          <ItemIcon size={12} />
                          {itemStatus.label}
                        </span>
                      </td>
                      <td className="p-4 font-mono text-sm text-slate-400">{item.job_name}</td>
                      <td className="p-4 font-mono text-sm text-slate-300">
                        {item.vm_name || <span className="text-slate-600">-</span>}
                      </td>
                      <td className="p-4 font-mono text-sm text-slate-400">{formatBytes(item.backup_size)}</td>
                      <td className="p-4 font-mono text-sm text-slate-400">{formatDuration(item.duration)}</td>
                      <td className="p-4 text-sm text-slate-500 max-w-xs truncate">{item.message || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerDetail;
